/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, May 2nd 2022, 9:53:18 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Typography from "@mui/material/Typography";
import { Permission, Team, TaskTemplate } from "lib/schemas";
import { useAPIListTeams } from "teams/services/TeamsAPI";
import { DataTable, HeadCell, ProgressWrapper, EmptyListWrapper, APIErrorWrapper, OSIconButton } from "ui-widgets";
import { EmptyTaskTeamsCTA } from "./EmptyTaskTeamsCTA";
import { getTaskAccessPermissionsWidgets } from "./TaskTypeWidgets";

interface TaskAllowedTeamsListProps {
  permissionIds: Permission["id"][];
  openTeamAccessForm: (team: Team) => void;
  allowedTeamsFecthDepArray: any[];
  taskTemplate: TaskTemplate;
}

const teamListHeadCells: HeadCell<Team, "team_access">[] = [
  {
    id: "team_name",
    numeric: false,
    disablePadding: false,
    label: "Team",
  },
  {
    id: "team_access",
    numeric: false,
    disablePadding: false,
    label: "Access",
  },
];

export const TaskAllowedTeamsList = ({
  permissionIds,
  openTeamAccessForm,
  allowedTeamsFecthDepArray,
  taskTemplate,
}: TaskAllowedTeamsListProps) => {
  const { teams, isMakingAPIRequest, httpResponseStatusCode, isAPIError } = useAPIListTeams({
    makeRequestOnInitialMount: Boolean(permissionIds?.length),
    apiQueryParams: { permission: permissionIds },
    dependencyList: allowedTeamsFecthDepArray,
  });

  const taskAccessPermissionsWidgets = getTaskAccessPermissionsWidgets(taskTemplate);

  return (
    <ProgressWrapper showLoader={isMakingAPIRequest}>
      <APIErrorWrapper isAPIError={isAPIError} httpResponseStatusCode={httpResponseStatusCode}>
        <EmptyListWrapper
          items={teams}
          noDataMessage={<EmptyTaskTeamsCTA taskTitle={taskTemplate?.title} openTeamAccessForm={openTeamAccessForm} />}
        >
          <DataTable<Team, "team_access">
            title=""
            headCells={teamListHeadCells}
            defaultOrderBy="team_name"
            initialRowsPerPage={10}
            dataSource={teams}
            uniqueKey="id"
            searchKeys={null}
            unit="Permitted Team"
            titleKey="team_name"
            renderRowActions={(team) => (
              <OSIconButton
                onClick={() => openTeamAccessForm(team)}
                color="primary"
                toolTipText={<Typography>Edit Team Permission for this task</Typography>}
              />
            )}
            listViewMode="table"
            dense={true}
            showToolbar={false}
            cellRenderProps={{
              team_access: (team: Team) => (
                <>
                  {taskAccessPermissionsWidgets
                    .filter((widget) => team.permissions.includes(widget.permissionId))
                    .map((widget) => widget.title)
                    .join(" | ")}
                </>
              ),
            }}
            allowRowSelection={false}
          />
        </EmptyListWrapper>
      </APIErrorWrapper>
    </ProgressWrapper>
  );
};
